import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Form, Badge, Row, Col, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { PDFLink } from './Reportv2';
import Loading from '../components/Loading';
import { getMonthlyReport } from '../services/OverTimeRequestService';
import { getActiveStatus, getAllUsers } from '../services/Services';

/**
 * Reporte anual de tiempo extra (frontend)
 * -------------------------------------------------------
 * Responsabilidades principales:
 * 1) Permite seleccionar: Meses (checkbox), Año, Estatus, Usuario.
 * 2) Carga totales por mes (horas + pago) desde backend (getMonthlyReport).
 * 3) Permite ver detalle por mes (modal): total horas, total pagado, desglose por razón (conteo + total $).
 * 4) Genera vista previa (modal fullscreen) que replica el contenido del Excel.
 * 5) Exporta Excel con 2 hojas: Resumen y Detalle (totales arriba).
 *
 * Estructuras clave:
 * - monthTotals: { [monthNumber]: { totalHours, totalPayment, requests } }
 * - requests: arreglo de solicitudes del mes que incluye:
 *   - items: arreglo de días/ítems con horas y pagos (horasToPay, moneyToPay, modifiedPayment, reason)
 *   - formattedExtraPayments: pagos extra con (pay, reason)
 */
const MONTHS = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

/**
 * Rango de años disponibles.
 * Si cambia el requisito (ej. permitir 2023-actual),
 * solo hay que modificar este arreglo.
 */
const YEARS = [2024, 2025, 2026];

/**
 * Formatea moneda (MXN) con separador de miles: $17,443.00
 * Se usa en tablas, modales y preview.
 */
const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "$0.00";

    return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
};

const AnualRequests = () => {
    const COLORS = ["#4e73df", "#1cc88a", "#36b9cc", "#f6c23e", "#e74a3b"];
    // =========================
    // ESTADO GENERAL / CARGA
    // =========================
    const [loading, setLoading] = useState(true);  // bandera de carga inicial (users/status)
    const [error, setError] = useState(null);      // error de carga inicial

    // =========================
    // CATALOGOS
    // =========================
    const [users, setUsers] = useState([]);        // listado de usuarios para dropdown
    const [status, setStatus] = useState([]);      // listado de estatus activos (id, name)

    // =========================
    // FILTROS
    // =========================
    const [year, setYear] = useState(2025);        // año del reporte
    const [selectedMonths, setSelectedMonths] = useState([]);   // meses seleccionados (números 1..12)
    const [selectedUserId, setSelectedUserId] = useState("");   // usuario seleccionado (string id); "" = Todos
    const [selectedStatusId, setSelectedStatusId] = useState(""); // estatus seleccionado; "" = no seleccionado

    // =========================
    // DATA DEL REPORTE
    // =========================
    /**
     * monthTotals almacena la respuesta del backend por mes.
     * Estructura:
     * {
     *   1: { totalHours: 99, totalPayment: 17443.00, requests: [...] },
     *   2: { ... },
     * }
     */
    const [monthTotals, setMonthTotals] = useState({});

    // =========================
    // MODALES
    // =========================
    const [showMonthDetailModal, setShowMonthDetailModal] = useState(false); // modal de detalle mensual
    const [userMonthSummary, setUserMonthSummary] = useState(null);          // datos del modal de detalle mensual

    const [showPreviewModal, setShowPreviewModal] = useState(false);         // modal preview (fullscreen)

    const [showChartsModal, setShowChartsModal] = useState(false);

    // =====================================================
    // EFECTO #1: cargar catálogos (users + status) al cambiar año
    // =====================================================
    useEffect(() => {
        /**
         * Se vuelve a pedir users/status al cambiar year.
         * (Podrías optimizarlo para que no dependa de year, si lo deseas.)
         * También se limpian meses al cambiar de año, para evitar mezclar selección anterior.
         */
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Users
                const allUsers = await getAllUsers();
                const list = Array.isArray(allUsers) ? allUsers : (allUsers?.users ?? []);
                setUsers(list);

                // Limpia selección de meses al cambiar año (evita mostrar data vieja del año anterior)
                setSelectedMonths([]);

                // Status
                const activeStatus = await getActiveStatus();
                setStatus(activeStatus || []);
            } catch (e) {
                setError(e);
                toast.error("Error fetching data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [year]);

    // =====================================================
    // EFECTO #2: cargar data por meses cada vez que cambian filtros
    // =====================================================
    useEffect(() => {
        /**
         * IMPORTANTE:
         * - Si no hay estatus seleccionado, NO cargamos nada.
         *   (Esto evita el bug donde se mostraba info con estatus default)
         * - Si no hay meses seleccionados, limpiamos monthTotals.
         */
        const fetchMonthsData = async () => {
            try {
                // Limpia el detalle mensual si cambian filtros (evita mostrar resumen viejo)
                setUserMonthSummary(null);

                // Reglas de validación (no hacemos requests)
                if (!selectedStatusId) {
                    setMonthTotals({});
                    return;
                }

                if (!selectedMonths.length) {
                    setMonthTotals({});
                    return;
                }

                const totals = {};

                // Por cada mes seleccionado, pedimos un reporte mensual al backend
                for (const month of selectedMonths) {
                    const resp = await getMonthlyReport(
                        year,
                        month,
                        selectedStatusId,
                        selectedUserId || 0 // backend: 0 = todos
                    );

                    totals[month] = {
                        totalHours: normalizeHours(resp?.totalHours),
                        totalPayment: normalizeMoney(resp?.totalToPay),
                        requests: resp?.requests || [],
                    };
                }

                setMonthTotals(totals);
            } catch (e) {
                toast.error("Error obteniendo datos por mes");
                console.error(e);
            }
        };

        fetchMonthsData();
    }, [selectedMonths, year, selectedStatusId, selectedUserId]);

    // =====================================================
    // Helpers de selección de meses
    // =====================================================
    /**
     * Agrega o quita un mes del arreglo selectedMonths.
     */
    const toggleMonth = (monthValue) => {
        setSelectedMonths((prev) =>
            prev.includes(monthValue)
                ? prev.filter((v) => v !== monthValue)
                : [...prev, monthValue]
        );
    };

    // =====================================================
    // Normalizadores (evitan NaN / undefined)
    // =====================================================
    const normalizeMoney = (n) => {
        const num = Number(n);
        return Number.isFinite(num) ? num : 0;
    };

    const normalizeHours = (n) => {
        const num = Number(n);
        return Number.isFinite(num) ? num : 0;
    };

    // =====================================================
    // Modal de detalle mensual: construye resumen de un mes
    // =====================================================
    /**
     * buildUserMonthSummary(month):
     * - Toma monthTotals[month].requests
     * - Si hay usuario seleccionado, filtra solo ese userId
     * - Suma totalHours y totalPayment a partir de:
     *    1) items (horas/pagos por día)
     *    2) formattedExtraPayments (pagos extra)
     * - Agrupa por razón:
     *    - Conteo = cuántas veces se repite la razón
     *    - Total  = suma de pagos asociados a esa razón
     * - Abre modal showMonthDetailModal
     */
    const buildUserMonthSummary = (month) => {
        const monthData = monthTotals[month];
        if (!monthData) return;

        const reqs = monthData.requests || [];

        // filtrar por usuario si aplica
        const filteredReqs = selectedUserId
            ? reqs.filter(r => String(r.userId) === String(selectedUserId))
            : reqs;

        let totalHours = 0;
        let totalPayment = 0;

        // Mapa: reason -> { reason, count, amount }
        const reasonMap = new Map();

        const addReason = (reasonRaw, amountRaw) => {
            const reason = (reasonRaw ?? "").toString().trim() || "Sin motivo";
            const amount = normalizeMoney(amountRaw);
            const current = reasonMap.get(reason) || { reason, count: 0, amount: 0 };
            current.count += 1;
            current.amount += amount;
            reasonMap.set(reason, current);
        };

        for (const r of filteredReqs) {
            // 1) ITEMS (horas)
            const items = Array.isArray(r.items) ? r.items : [];
            for (const it of items) {
                // hrsToPay: preferimos hoursToPay, fallback hours
                const hrsToPay = normalizeHours(it.hoursToPay ?? it.hours ?? 0);

                // pay: preferimos modifiedPayment (si existe), sino moneyToPay
                const pay = normalizeMoney(it.modifiedPayment ?? it.moneyToPay ?? 0);

                totalHours += hrsToPay;
                totalPayment += pay;

                // Agrupar por razón
                addReason(it.reason, pay);
            }

            // 2) EXTRA PAYMENTS (pagos extra)
            const extras = Array.isArray(r.formattedExtraPayments)
                ? r.formattedExtraPayments
                : [];
            for (const ex of extras) {
                const pay = normalizeMoney(ex.pay ?? 0);
                totalPayment += pay;

                // Agrupar por razón
                addReason(ex.reason, pay);
            }
        }

        setUserMonthSummary({
            month,
            totalHours,
            totalPayment,
            reasons: Array.from(reasonMap.values()).sort((a, b) => b.amount - a.amount),
        });

        setShowMonthDetailModal(true);
    };

    // =====================================================
    // buildReportData: datos base para PREVIEW y EXCEL
    // =====================================================
    /**
     * buildReportData():
     * Devuelve:
     * - summaryRows: filas para tabla resumen (mes, horas, pago, usuario, estatus)
     * - detailRows: filas para tabla detalle (año, mes, usuario, razón, conteo, total)
     *
     * NOTA: El detalle reconstruye el "reasonMap" por mes, para que coincida con lo mostrado en modal.
     * Si cambian reglas de cálculo (por ejemplo separar "items" vs "extras"),
     * hay que actualizar aquí y en buildUserMonthSummary para mantener consistencia.
     */
    const buildReportData = () => {
        const getActiveStatus = (id) => {
            const sid = Number(id || 5);
            return status.find(s => Number(s.id) === sid)?.name ?? `Estatus ${sid}`;
        };

        // ========= RESUMEN =========
        const summaryRows = selectedMonths
            .slice()
            .sort((a, b) => a - b)
            .map(m => {
                const label = MONTHS.find(x => x.value === m)?.label ?? `Mes ${m}`;
                const t = monthTotals[m];
                return {
                    Año: year,
                    Mes: label,
                    Horas: t ? t.totalHours : 0,
                    Pago: t ? t.totalPayment : 0,
                    Usuario: selectedUserLabel ?? "Todos",
                    Estatus: getActiveStatus(selectedStatusId || 5),
                };
            });

        // ========= DETALLE =========
        const detailRows = [];
        for (const m of selectedMonths) {
            const monthData = monthTotals[m];
            if (!monthData) continue;

            const reqs = monthData.requests || [];
            const filteredReqs = selectedUserId
                ? reqs.filter(r => String(r.userId) === String(selectedUserId))
                : reqs;

            const reasonMap = new Map();
            const addReason = (reasonRaw, amountRaw) => {
                const reason = (reasonRaw ?? "").toString().trim() || "Sin motivo";
                const amount = Number(amountRaw) || 0;
                const cur = reasonMap.get(reason) || { reason, count: 0, amount: 0 };
                cur.count += 1;
                cur.amount += amount;
                reasonMap.set(reason, cur);
            };

            for (const r of filteredReqs) {
                // Items: solo usamos pagos para el detalle
                const items = Array.isArray(r.items) ? r.items : [];
                for (const it of items) {
                    const pay = Number(it.modifiedPayment ?? it.moneyToPay ?? 0) || 0;
                    addReason(it.reason, pay);
                }

                // Extras
                const extras = Array.isArray(r.formattedExtraPayments)
                    ? r.formattedExtraPayments
                    : [];
                for (const ex of extras) {
                    const pay = Number(ex.pay ?? 0) || 0;
                    addReason(ex.reason, pay);
                }
            }

            const label = MONTHS.find(x => x.value === m)?.label ?? `Mes ${m}`;
            for (const r of Array.from(reasonMap.values()).sort((a, b) => b.amount - a.amount)) {
                detailRows.push({
                    Año: year,
                    Mes: label,
                    Usuario: selectedUserLabel ?? "Todos",
                    Razón: r.reason,
                    Conteo: r.count,
                    Total: r.amount,
                });
            }
        }

        return { summaryRows, detailRows };
    };

    // =====================================================
    // Exportación Excel (2 hojas)
    // =====================================================
    /**
     * exportToExcel():
     * - Construye data con buildReportData()
     * - Hoja 1 (Resumen): totales anuales arriba + tabla resumen
     * - Hoja 2 (Detalle): totales (conteo y $) arriba + tabla detalle
     *
     * Si cambia el formato del Excel (nuevo layout/columnas),
     * la modificación se hace principalmente aquí.
     */
    const exportToExcel = () => {
        const { summaryRows, detailRows } = buildReportData();

        // Totales anuales (solo de lo seleccionado)
        const totalHoursAnnual = summaryRows.reduce((acc, r) => acc + (Number(r.Horas) || 0), 0);
        const totalPayAnnual = summaryRows.reduce((acc, r) => acc + (Number(r.Pago) || 0), 0);

        const wb = XLSX.utils.book_new();

        // =========================
        // HOJA 1: RESUMEN
        // =========================
        const summaryAoa = [];

        // Totales arriba
        summaryAoa.push(["", "", "Total Horas", "Total Pago"]);
        summaryAoa.push(["", "", totalHoursAnnual, totalPayAnnual]);
        summaryAoa.push([]); // fila en blanco

        // Encabezados
        summaryAoa.push(["Año", "Mes", "Horas", "Pago", "Usuario", "Estatus"]);

        // Filas
        for (const r of summaryRows) {
            summaryAoa.push([
                r.Año,
                r.Mes,
                Number(r.Horas) || 0,
                Number(r.Pago) || 0,
                r.Usuario,
                r.Estatus
            ]);
        }

        const ws1 = XLSX.utils.aoa_to_sheet(summaryAoa);

        // Formato de moneda en columna D (Pago) y en total (D2)
        // D2 = fila 2 col D, D5.. abajo
        ws1["!cols"] = [
            { wch: 8 },  // Año
            { wch: 12 }, // Mes
            { wch: 12 }, // Horas
            { wch: 14 }, // Pago
            { wch: 28 }, // Usuario
            { wch: 14 }, // Estatus
        ];

        XLSX.utils.book_append_sheet(wb, ws1, "Resumen");

        // =========================
        // HOJA 2: DETALLE
        // =========================
        if (detailRows.length) {
            const totalConteo = detailRows.reduce((acc, r) => acc + (Number(r.Conteo) || 0), 0);
            const totalPagoDetalle = detailRows.reduce((acc, r) => acc + (Number(r.Total) || 0), 0);

            const detailAoa = [];
            detailAoa.push(["", "", "", "", "Total Conteo", "Total Pago"]);
            detailAoa.push(["", "", "", "", totalConteo, totalPagoDetalle]);
            detailAoa.push([]);

            detailAoa.push(["Año", "Mes", "Usuario", "Razón", "Conteo", "Total"]);

            for (const r of detailRows) {
                detailAoa.push([
                    r.Año,
                    r.Mes,
                    r.Usuario,
                    r["Razón"],
                    Number(r.Conteo) || 0,
                    Number(r.Total) || 0
                ]);
            }

            const ws2 = XLSX.utils.aoa_to_sheet(detailAoa);

            ws2["!cols"] = [
                { wch: 8 },   // Año
                { wch: 12 },  // Mes
                { wch: 28 },  // Usuario
                { wch: 60 },  // Razón
                { wch: 12 },  // Conteo
                { wch: 14 },  // Total
            ];

            XLSX.utils.book_append_sheet(wb, ws2, "Detalle");
        }

        const fileName = `Reporte_${year}_${selectedUserLabel ? selectedUserLabel.replaceAll(" ", "_") : "Todos"}.xlsx`;
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([wbout], { type: "application/octet-stream" }), fileName);
    };

    // =====================================================
    // Datos derivados para UI (labels, títulos y totales)
    // =====================================================
    /**
     * selectedUserLabel:
     * - texto del usuario seleccionado.
     * - null si no se seleccionó (Todos)
     */
    const selectedUserLabel = selectedUserId
        ? (users.find((u) => String(u.id) === String(selectedUserId))?.userName
            ?? users.find((u) => String(u.id) === String(selectedUserId))?.email
            ?? `Usuario ${selectedUserId}`)
        : null;

    /**
     * totalsTitle:
     * - Título que cambia según sea Todos o usuario específico.
     */
    const totalsTitle = selectedUserLabel
        ? `Totales por mes de ${selectedUserLabel}`
        : "Totales por mes (todos los usuarios)";

    // Meses ordenados para render y totales
    const selectedMonthsSorted = selectedMonths.slice().sort((a, b) => a - b);

    // Totales de lo seleccionado (se muestran en tabla principal y preview)
    const totalHoursSelected = selectedMonthsSorted.reduce((acc, m) => {
        const t = monthTotals[m];
        return acc + (t?.totalHours ?? 0);
    }, 0);

    const totalPaySelected = selectedMonthsSorted.reduce((acc, m) => {
        const t = monthTotals[m];
        return acc + (t?.totalPayment ?? 0);
    }, 0);

    // Construimos las filas para preview y excel desde la misma función (consistencia)
    const { summaryRows, detailRows } = buildReportData();

    // Totales del detalle para el preview (arriba del detalle)
    const totalConteoDetalle = detailRows.reduce((acc, r) => acc + (Number(r.Conteo) || 0), 0);
    const totalPagoDetalle = detailRows.reduce((acc, r) => acc + (Number(r.Total) || 0), 0);

    // Total conteo del modal mensual (solo para userMonthSummary actual)
    const totalConteo =
        userMonthSummary?.reasons?.reduce((acc, r) => acc + (Number(r.count) || 0), 0) ?? 0;

    // =====================================================
    // Helpers: Normalizar horas/pago - concepto/subconcepto/detalle
    // =====================================================
    const splitReason = (reason) => {
        const raw = (reason ?? "").toString().trim();
        if (!raw) return { concept: "", subconcept: "", detail: "" };
        const parts = raw.split("/").map(p => p.trim()).filter(Boolean);
        return {
            concept: parts[0] ?? "",
            subconcept: parts[1] ?? "",
            detail: parts.slice(2).join(" / ") ?? ""
        };
    };

    const resolveConcept = (item) => {
        // Si ya viene normalizado, úsalo
        const c = (item?.concept ?? "").toString().trim();
        if (c) return c;

        // Fallback a reason
        const rr = splitReason(item?.reason);
        return rr.concept || "Sin concepto";
    };

    // =====================================================
    // Cálculos de insights
    // =====================================================
    const computeInsights = () => {
        const months = selectedMonthsSorted; // ya lo tienes derivado
        if (!months.length) return null;

        // 1) Mes con más horas (usa totals que ya te da backend)
        let bestMonth = null;
        let bestMonthHours = -1;

        for (const m of months) {
            const t = monthTotals[m];
            const hours = normalizeHours(t?.totalHours ?? 0);
            if (hours > bestMonthHours) {
                bestMonthHours = hours;
                bestMonth = m;
            }
        }

        // 2) Stats por usuario y 3) Stats por concepto (horas)
        const userMap = new Map();     // userId -> { userId, userName, hours, pay }
        const conceptMap = new Map();  // concept -> hours

        for (const m of months) {
            const monthData = monthTotals[m];
            const reqs = Array.isArray(monthData?.requests) ? monthData.requests : [];

            for (const r of reqs) {
                const uid = String(r.userId ?? "");
                const uname =
                    r.userName ??
                    r.user ??
                    r.email ??
                    getUserLabelById(uid);

                // si estás filtrando por usuario, respeta eso
                if (selectedUserId && String(selectedUserId) !== uid) continue;

                const curU = userMap.get(uid) || { userId: uid, userName: uname, hours: 0, pay: 0 };

                // ITEMS (horas y pago)
                const items = Array.isArray(r.items) ? r.items : [];
                for (const it of items) {
                    const hrs = normalizeHours(it.hoursToPay ?? it.hours ?? 0);
                    const pay = normalizeMoney(it.modifiedPayment ?? it.moneyToPay ?? 0);

                    curU.hours += hrs;
                    curU.pay += pay;

                    const concept = resolveConcept(it);
                    conceptMap.set(concept, (conceptMap.get(concept) || 0) + hrs);
                }

                // EXTRAS (solo pago)
                const extras = Array.isArray(r.formattedExtraPayments) ? r.formattedExtraPayments : [];
                for (const ex of extras) {
                    curU.pay += normalizeMoney(ex.pay ?? 0);
                }

                userMap.set(uid, curU);
            }
        }

        const users = Array.from(userMap.values());

        // Usuario con más horas
        const topUserByHours = users.reduce((best, u) => (!best || u.hours > best.hours ? u : best), null);

        // Usuario con más pago
        const topUserByPay = users.reduce((best, u) => (!best || u.pay > best.pay ? u : best), null);

        // Concepto con más horas
        let topConcept = null;
        let topConceptHours = -1;
        for (const [concept, hours] of conceptMap.entries()) {
            if (hours > topConceptHours) {
                topConceptHours = hours;
                topConcept = concept;
            }
        }

        // “Promedios” (interpretación más útil aquí):
        // promedio mensual en los meses seleccionados (no solo meses con data, para que sea consistente)
        const monthsCount = months.length || 1;

        const avgTopHours = topUserByHours ? (topUserByHours.hours / monthsCount) : 0;
        const avgTopPay = topUserByPay ? (topUserByPay.pay / monthsCount) : 0;

        return {
            bestMonth,
            bestMonthHours,
            topUserByHours,
            avgTopHours,
            topUserByPay,
            avgTopPay,
            topConcept,
            topConceptHours,
            users,
            conceptMap,
        };
    };

    // =====================================================
    // Datos para gráficas
    // =====================================================
    const buildChartsData = (insights) => {
        const months = selectedMonthsSorted;

        // Horas por mes
        const hoursByMonth = months.map(m => ({
            month: MONTHS.find(x => x.value === m)?.label ?? `Mes ${m}`,
            hours: normalizeHours(monthTotals[m]?.totalHours ?? 0),
            pay: normalizeMoney(monthTotals[m]?.totalPayment ?? 0),
        }));

        // Top 5 usuarios por horas
        const topUsersByHours = insights.users
            .slice()
            .sort((a, b) => b.hours - a.hours)
            .slice(0, 5)
            .map(u => ({
                // Si quieres mostrar el nombre completo:
                name: u.userName,

                // Si prefieres cortar para que no se encime en el eje X:
                // name: shortenLabel(u.userName, 20),

                value: u.hours,
            }));

        // Top 5 usuarios por pago
        const topUsersByPay = insights.users
            .slice()
            .sort((a, b) => b.pay - a.pay)
            .slice(0, 5)
            .map(u => ({
                name: u.userName,
                // name: shortenLabel(u.userName, 20),
                value: u.pay,
            }));

        // Top 5 conceptos por horas
        const topConcepts = Array.from(insights.conceptMap.entries())
            .map(([concept, hours]) => ({ name: concept, value: hours }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 5);

        return { hoursByMonth, topUsersByHours, topUsersByPay, topConcepts };
    };

    // =====================================================
    // Helpers: nombre de usuario por ID (usando catálogo users)
    // =====================================================
    const getUserLabelById = (userId) => {
        const uid = String(userId ?? "");
        if (!uid) return "Usuario N/D";

        const u =
            users.find((x) => String(x.id) === uid) ||
            users.find((x) => String(x.userId) === uid); // por si el shape cambia

        // Prioridad: userName -> name -> email -> fallback
        return (
            u?.userName ??
            u?.name ??
            u?.email ??
            `Usuario ${uid}`
        );
    };

    // Para labels largos en gráficas (opcional)
    const shortenLabel = (s, max = 18) => {
        const txt = (s ?? "").toString();
        if (txt.length <= max) return txt;
        return txt.slice(0, max - 1) + "…";
    };

    // Render condicional: loading / error
    if (loading) return <Loading />;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <div>
            <h2>Solicitudes:</h2>
            {/* =========================
                SECCIÓN FILTROS
                ========================= */}
            <Row className="mt-3">
                {/* Columna izquierda: meses */}
                <Col xl={8}>
                    <Form.Group>
                        <Form.Label>Meses</Form.Label>
                        {/* CHECKBOXES de meses (1..12) */}
                        <div className="d-flex flex-wrap gap-3">
                            {MONTHS.map((m) => (
                                <Form.Check
                                    key={m.value}
                                    type="checkbox"
                                    id={`month-${m.value}`}
                                    label={m.label}
                                    checked={selectedMonths.includes(m.value)}
                                    onChange={() => toggleMonth(m.value)}
                                />
                            ))}
                        </div>

                        {MONTHS.length === 0 && (
                            <small className="text-muted">No hay meses disponibles para este año.</small>
                        )}
                        {/* Acciones rápidas: seleccionar todos / limpiar */}
                        <div className="mt-2 d-flex gap-2">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setSelectedMonths(MONTHS.map((m) => m.value))}
                                disabled={MONTHS.length === 0}
                            >
                                Seleccionar todos
                            </Button>

                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setSelectedMonths([])}
                                disabled={MONTHS.length === 0}
                            >
                                Limpiar
                            </Button>
                        </div>
                    </Form.Group>
                </Col>
                {/* Columna derecha: año, estatus, usuario */}
                <Col xl={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Año</Form.Label>
                        <Form.Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                            {YEARS.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Estatus de la solicitud</Form.Label>
                        <Form.Select value={selectedStatusId} onChange={(e) => setSelectedStatusId(e.target.value)}>
                            <option value="">Seleccionar</option>
                            {status.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mt-3">
                        <Form.Label>Usuario</Form.Label>
                        <Form.Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                            <option value="">Todos</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.userName ?? u.email ?? `Usuario ${u.id}`}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            {/* =========================
                ACCIONES: Excel y Preview
                ========================= */}
            <div className="mt-3 d-flex gap-2">
                <Button
                    variant="success"
                    disabled={!selectedMonths.length || !selectedStatusId}
                    onClick={exportToExcel}
                >
                    Descargar Excel
                </Button>

                <Button
                    variant="primary"
                    disabled={!selectedMonths.length || !selectedStatusId}
                    onClick={() => setShowPreviewModal(true)}
                >
                    Visualizar
                </Button>

                <Button
                    variant="info"
                    disabled={!selectedMonths.length || !selectedStatusId}
                    onClick={() => setShowChartsModal(true)}
                >
                    Ver gráficas
                </Button>
            </div>
            {/* =========================
                TABLA PRINCIPAL (Totales por mes)
                Se renderiza SOLO si hay meses y estatus seleccionado.
                ========================= */}
            {selectedMonths.length > 0 && selectedStatusId ? (
                <div className="mt-4">
                    <h5>{totalsTitle}</h5>

                    <Table bordered responsive>
                        <thead>
                            <tr className="table-primary">
                                <th>Mes</th>
                                <th className="text-center">Horas</th>
                                <th className="text-center">Pago</th>
                                <th className="text-center">Ver</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedMonths
                                .sort((a, b) => a - b)
                                .map(month => {
                                    const mLabel = MONTHS.find(x => x.value === month)?.label ?? month;
                                    const t = monthTotals[month];
                                    return (
                                        <tr key={month}>
                                            <td>{mLabel}</td>
                                            <td className="text-center">{t ? t.totalHours : "-"}</td>
                                            <td className="text-center">{t ? formatCurrency(t.totalPayment) : "-"}</td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    disabled={!t || !selectedStatusId}
                                                    onClick={() => buildUserMonthSummary(month)}
                                                >
                                                    Ver detalle
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                        <tfoot>
                            <tr className="table-primary">
                                <td className="text-end"><b>Totales:</b></td>
                                <td className="text-center"><b>{totalHoursSelected}</b></td>
                                <td className="text-center"><b>{formatCurrency(totalPaySelected)}</b></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            ) : selectedMonths.length > 0 && !selectedStatusId ? (
                <div className="mt-4 text-muted">
                    Selecciona un <b>estatus</b> para mostrar la información.
                </div>
            ) : null}
            {/* =========================
                MODAL DETALLE MENSUAL
                - Muestra totales y razones del mes seleccionado
                - Conteo y total por razón
                ========================= */}
            <Modal
                show={showMonthDetailModal}
                onHide={() => setShowMonthDetailModal(false)}
                size="xl"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {userMonthSummary
                            ? `Detalle ${MONTHS.find(x => x.value === userMonthSummary.month)?.label} ${year}`
                            : "Detalle"}
                        {selectedUserId ? " (usuario seleccionado)" : " (todos)"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {!userMonthSummary ? (
                        <div className="text-muted">Sin información para mostrar.</div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div><b>Total horas:</b> {userMonthSummary.totalHours}</div>
                                </Col>
                                <Col md={4}>
                                    <div><b>Total conteo:</b> {totalConteo}</div>
                                </Col>
                                <Col md={4}>
                                    <div><b>Total pagado:</b> {formatCurrency(userMonthSummary.totalPayment)}</div>
                                </Col>
                            </Row>
                            <h6>Razones</h6>
                            <Table bordered responsive>
                                <thead>
                                    <tr className="table-primary">
                                        <th>Razón</th>
                                        <th className="text-center">Conteo</th>
                                        <th className="text-center">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userMonthSummary.reasons.map(r => (
                                        <tr key={r.reason}>
                                            <td>{r.reason}</td>
                                            <td className="text-center">{r.count}</td>
                                            <td className="text-center">{formatCurrency(r.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMonthDetailModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* =========================
                MODAL PREVIEW (FULLSCREEN)
                - Debe ser espejo del Excel:
                    Resumen con totales arriba + tabla
                    Detalle con totales arriba + tabla
                ========================= */}
            <Modal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                size="xl"
                centered
                scrollable
                fullscreen
            >
                <Modal.Header className="d-flex justify-content-between align-items-center">
                    <Modal.Title>
                        Visualización del reporte {year} {selectedUserLabel && `- ${selectedUserLabel}`}
                    </Modal.Title>

                    <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-3"
                        onClick={() => setShowPreviewModal(false)}
                    >
                        ✕ Cerrar
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    {/* ================= RESUMEN ================= */}
                    <Row className="mb-3">
                        <Col md={6}>
                            <div><b>Total horas:</b> {totalHoursSelected}</div>
                        </Col>
                        <Col md={6}>
                            <div><b>Total pago:</b> {formatCurrency(totalPaySelected)}</div>
                        </Col>
                    </Row>

                    <h6 className="mb-2">Resumen</h6>
                    <Table bordered responsive>
                        <thead>
                            <tr className="table-primary">
                                <th>Mes</th>
                                <th className="text-center">Horas</th>
                                <th className="text-center">Pago</th>
                                <th className="text-center">Usuario</th>
                                <th className="text-center">Estatus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {summaryRows.map((r, idx) => (
                                <tr key={`${r.Mes}-${idx}`}>
                                    <td>{r.Mes}</td>
                                    <td className="text-center">{r.Horas}</td>
                                    <td className="text-center">{formatCurrency(r.Pago)}</td>
                                    <td className="text-center">{r.Usuario}</td>
                                    <td className="text-center">{r.Estatus}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* ================= DETALLE ================= */}
                    <hr />

                    <Row className="mb-3">
                        <Col md={6}>
                            <div><b>Total conteo:</b> {totalConteoDetalle}</div>
                        </Col>
                        <Col md={6}>
                            <div><b>Total pago:</b> {formatCurrency(totalPagoDetalle)}</div>
                        </Col>
                    </Row>

                    <h6 className="mb-2">Detalle</h6>
                    {detailRows.length === 0 ? (
                        <div className="text-muted">No hay detalle para los filtros seleccionados.</div>
                    ) : (
                        <Table bordered responsive>
                            <thead>
                                <tr className="table-primary">
                                    <th>Año</th>
                                    <th>Mes</th>
                                    <th>Usuario</th>
                                    <th>Razón</th>
                                    <th className="text-center">Conteo</th>
                                    <th className="text-center">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailRows.map((r, idx) => (
                                    <tr key={`${r.Mes}-${r["Razón"]}-${idx}`}>
                                        <td>{r.Año}</td>
                                        <td>{r.Mes}</td>
                                        <td>{r.Usuario}</td>
                                        <td>{r["Razón"]}</td>
                                        <td className="text-center">{r.Conteo}</td>
                                        <td className="text-center">{formatCurrency(r.Total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>
            {/* =========================
                MODAL GRÁFICO
                - Muestra gráficas del reporte mensual y anual
                ========================= */}
            {(() => {
                const insights = computeInsights();
                if (!insights) return null;

                const charts = buildChartsData(insights);

                return (
                    <Modal
                        show={showChartsModal}
                        onHide={() => setShowChartsModal(false)}
                        size="xl"
                        centered
                        scrollable
                        fullscreen
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Indicadores y gráficas</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            {/* ===== KPIs rápidos ===== */}
                            <Row className="mb-3">
                                <Col md={4}>
                                    <div><b>Mes con más horas:</b> {MONTHS.find(x => x.value === insights.bestMonth)?.label ?? "-"} ({insights.bestMonthHours})</div>
                                </Col>
                                <Col md={4}>
                                    <div><b>Usuario con más horas:</b> {insights.topUserByHours?.userName ?? "-"} ({insights.topUserByHours?.hours ?? 0})</div>
                                    <div className="text-muted">Promedio mensual: {insights.avgTopHours.toFixed(2)}</div>
                                </Col>
                                <Col md={4}>
                                    <div><b>Usuario con más pago:</b> {insights.topUserByPay?.userName ?? "-"} ({formatCurrency(insights.topUserByPay?.pay ?? 0)})</div>
                                    <div className="text-muted">Promedio mensual: {formatCurrency(insights.avgTopPay)}</div>
                                </Col>
                            </Row>

                            <Row className="mb-3">
                                <Col md={12}>
                                    <div><b>Concepto con más horas:</b> {insights.topConcept ?? "-"} ({insights.topConceptHours})</div>
                                </Col>
                            </Row>

                            <hr />

                            {/* ===== Gráfica 1: Horas por mes ===== */}
                            <h6>Horas por mes</h6>
                            <div style={{ width: "100%", height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={charts.hoursByMonth}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="hours" fill="#4e73df" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <hr />

                            {/* ===== Gráfica 2: Top usuarios por horas ===== */}
                            <h6>Top usuarios por horas</h6>
                            <div style={{ width: "100%", height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={charts.topUsersByHours}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value">
                                            {charts.topUsersByHours.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <hr />

                            {/* ===== Gráfica 3: Top usuarios por pago ===== */}
                            <h6>Top usuarios por pago</h6>
                            <div style={{ width: "100%", height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={charts.topUsersByPay}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value">
                                            {charts.topUsersByPay.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            <hr />

                            {/* ===== Gráfica 4: Top conceptos por horas ===== */}
                            <h6>Top conceptos por horas</h6>
                            <div style={{ width: "100%", height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={charts.topConcepts}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value">
                                            {charts.topConcepts.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Modal.Body>

                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowChartsModal(false)}>
                                Cerrar
                            </Button>
                        </Modal.Footer>
                    </Modal>
                );
            })()}
            <ToastContainer />
        </div>
    );
};

export default AnualRequests;